// Dependencies
import { createSlice, createAction } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
import { applyColumnResize, ColumnResizePayload } from '../../../utils/list/utils/columnResize';
// Types
import { Column } from '../objects/column';
import { DashboardSubscriptions } from '../objects/dashboardSubscriptions';
import { DashboardSubscriptionsState } from '../objects/dashboardSubscriptionsState';
import { Preferences } from '../../../profile/objects/preferences';

const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const initialState: DashboardSubscriptionsState = {
    columns: [
        // {
        //     label: 'name',
        //     field_name: 'NAME',
        //     checked: true,
        //     order_by: 'name',
        //     fixed_width: false,
        //     last_updated: false
        // },
        {
            label: 'name-of-report',
            field_name: 'REPORT_NAME',
            checked: true,
            order_by: 'report_name',
            fixed_width: false,
            last_updated: false,
            width: null
            },
        {
            label: 'report-format',
            field_name: 'FORMAT',
            checked: true,
            order_by: 'format',
            fixed_width: false,
            last_updated: false,
            width: null
            },
        {
            label: 'recipients-count',
            field_name: 'RECIPIENTS_COUNT',
            checked: true,
            order_by: '',
            fixed_width: false,
            last_updated: false,
            width: null
            },
        {
            label: 'sending-start-date',
            field_name: 'SENDING_START_DATE',
            checked: true,
            order_by: '',
            fixed_width: false,
            last_updated: false,
            width: null
            },
        {
            label: 'frequency',
            field_name: 'FREQUENCY',
            checked: true,
            order_by: '',
            fixed_width: false,
            last_updated: false,
            width: null
            },
        {
            label: 'validity-period',
            field_name: 'VALIDATION_PERIOD',
            checked: true,
            order_by: '',
            fixed_width: false,
            last_updated: false,
            width: null
            },
        {
            label: 'created-date',
            field_name: 'CREATED_DATE',
            checked: false,
            order_by: 'created_date',
            fixed_width: true,
            last_updated: false,
            width: null
            },
        {
            label: 'created-by',
            field_name: 'CREATED_BY',
            checked: false,
            order_by: 'creator__username',
            fixed_width: true,
            last_updated: false,
            width: null
            },
        {
            label: 'modified-date',
            field_name: 'MODIFIED_DATE',
            checked: true,
            order_by: 'modified_date',
            fixed_width: true,
            last_updated: false,
            width: null
            },
        {
            label: 'modified-by',
            field_name: 'MODIFIED_BY',
            checked: true,
            order_by: 'last_author__username',
            fixed_width: true,
            last_updated: false,
            width: null
            },
        {
            label: 'activated',
            field_name: 'ACTIVATED',
            checked: true,
            order_by: 'is_active',
            fixed_width: true,
            last_updated: false,
            width: null
            }
    ],
    count: 0,
    edit: null,
    export_full: false,
    export_list: false,
    export_type: 'CSV',
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_creator: '',
    filter_last_author: '',
    filter_modified_date_gte: null,
    filter_modified_date_lte: null,
    filter_open: false,
    filter_period: 'CREATION',
    filter_period_type: 0,
    limit: 50,
    list: null,
    order: 'asc',
    order_by: 'name',
    page: 0,
    preferences: [],
    refresh: false,
    dashboard_subscriptions: null,
    running: false,
    search: '',
    recipient: [],
};

export const dashboardSubscriptionsSlice = createSlice({
    name: 'dashboardSubscriptions',
    initialState,
    reducers: {
        addPreferences: (state, action) => {
            state.preferences = [...state.preferences, action.payload];
        },
        moveDownColumn: (state, action) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload + 1] };
            columns[action.payload + 1] = columns[action.payload];
            columns[action.payload + 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        moveUpColumn: (state, action) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload - 1] };
            columns[action.payload - 1] = columns[action.payload];
            columns[action.payload - 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        resetFilters: (state) => {
            state.filter_created_date_gte = null;
            state.filter_created_date_lte = null;
            state.filter_creator = '';
            state.filter_last_author = '';
            state.filter_modified_date_gte = null;
            state.filter_modified_date_lte = null;
            state.filter_period = 'CREATION';
            state.filter_period_type = 0;
        },
        resetDashboardSubscriptions: (state) => {
            state.edit = null;
            state.refresh = !state.refresh;
            state.dashboard_subscriptions = null;
        },
        setEdit: (state, action) => {
            state.edit = action.payload;
            if (state.dashboard_subscriptions) {
                state.recipients = state.dashboard_subscriptions.recipients;
            }
        },
        setExportType: (state, action) => {
            state.export_type = action.payload;
        },
        setLimit: (state, action) => {
            state.list = null;
            state.limit = action.payload;
        },
        setList: (state, action) => {
            state.count = action.payload.count;
            state.list = action.payload.results;
            state.running = false;
        },
        setOrderBy: (state, action) => {
            state.list = null;
            state.order_by = action.payload;
            state.order = state.order_by === action.payload && state.order === 'asc' ? 'desc' : 'asc';
        },
        setPage: (state, action) => {
            state.list = null;
            state.page = action.payload;
        },
        setDashboardSubscriptions: (state, action: PayloadAction<DashboardSubscriptions>) => {
            state.edit = null;
            state.dashboard_subscriptions = action.payload;
        },
        setDashboardSubscriptionsPreferences: (state, action) => {
            state.preferences = action.payload;
        },
        setRunning: (state, action) => {
            state.running = action.payload;
        },
        setSearch: (state, action) => {
            state.list = null;
            state.page = 0;
            state.search = action.payload;
        },
        toggleColumn: (state, action) => {
            const columns = [...state.columns];
            const column = { ...columns[action.payload] };
            column.checked = !column.checked;
            columns[action.payload] = column;
            state.columns = columns;
        },
        toggleExport: (state, action) => {
            state.export_list = action.payload;
        },
        toggleExportFull: (state) => {
            state.export_full = !state.export_full;
        },
        toggleFilters: (state) => {
            state.filter_open = !state.filter_open;
        },
        toggleRefresh: (state) => {
            state.refresh = !state.refresh;
        },
        updateFilterCreatedDateGte: (state, action) => {
            state.filter_created_date_gte = action.payload;
        },
        updateFilterCreator: (state, action) => {
            state.filter_created_date_lte = action.payload;
        },
        updateFilterCreatedDateLte: (state, action) => {
            state.filter_creator = action.payload;
        },
        updateFilterLastAuthor: (state, action) => {
            state.filter_last_author = action.payload;
        },
        updateFilterModifiedDateGte: (state, action) => {
            state.filter_modified_date_gte = action.payload;
        },
        updateFilterModifiedDateLte: (state, action) => {
            state.filter_modified_date_lte = action.payload;
        },
        updateFilterPeriod: (state, action) => {
            state.filter_period = action.payload;
            state.filter_period_type = 0;
            state.filter_created_date_gte = action.payload === 'MODIFICATION' ? null : state.filter_created_date_gte;
            state.filter_created_date_lte = action.payload === 'MODIFICATION' ? null : state.filter_created_date_lte;
            state.filter_modified_date_gte = action.payload === 'CREATION' ? null : state.filter_modified_date_gte;
            state.filter_modified_date_lte = action.payload === 'CREATION' ? null : state.filter_modified_date_lte;
        },
        updateFilterPeriodType: (state, action) => {
            state.filter_period_type = action.payload;
        },
        updateListItemActivated: (state, action) => {
            if (state.list !== null) {
                const list = [...state.list];
                for (let i = 0; i < list.length; i++) {
                    if (list[i].id === action.payload.id) {
                        list[i] = { ...list[i], is_active: action.payload.is_active };
                        break;
                    }
                }
                state.list = list;
            }
        },
        setColumns: (state, action: PayloadAction<Column[]>) => {
            state.columns = action.payload;
        },
        resizeColumn: (state, action: PayloadAction<ColumnResizePayload>) => {
            state.columns = applyColumnResize(state.columns, action.payload);
        },
        addRecipient: (state, action) => {
            state.recipients.push(action.payload);
        },
        removeRecipient: (state, action) => {
            state.recipients.splice(action.payload, 1);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, (state, action) => {
                const field_name: string[] = [];
                const columns: Column[] = [];
                const preferences: Preferences[] = [];
                let order_by = '-created_date';
                let order: 'asc' | 'desc' = 'asc';
                action.payload.map((preference: Preferences) => {
                    if (preference.module === 'DASHBOARD_SUBSCRIPTIONS') {
                        if (!field_name.includes(preference.field_name)) {
                            switch (preference.field_name) {
                                case 'REPORT_NAME':
                                    columns.push({
                                        label: 'name-of-report',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'report_name',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? 'report_name' : order_by;
                                    break;
                                case 'FORMAT':
                                    columns.push({
                                        label: 'report-format',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'format',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? 'format' : order_by;
                                    break;
                                case 'RECIPIENTS_COUNT':
                                    columns.push({
                                        label: 'recipients-count',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: '',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? '' : order_by;
                                    break;
                                case 'SENDING_START_DATE':
                                    columns.push({
                                        label: 'sending-start-date',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: '',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? '' : order_by;
                                    break;
                                case 'FREQUENCY':
                                    columns.push({
                                        label: 'frequency',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: '',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? '' : order_by;
                                    break;
                                case 'VALIDATION_PERIOD':
                                    columns.push({
                                        label: 'validity-period',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: '',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? '' : order_by;
                                    break;
                                case 'CREATED_DATE':
                                    columns.push({
                                        label: 'created-date',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'created_date',
                                        fixed_width: true,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? 'created_date' : order_by;
                                    break;
                                case 'CREATED_BY':
                                    columns.push({
                                        label: 'created-by',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'creator__username',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? 'creator__username' : order_by;
                                    break;
                                case 'MODIFIED_DATE':
                                    columns.push({
                                        label: 'modified-date',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'modified_date',
                                        fixed_width: true,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? 'modified_date' : order_by;
                                    break;
                                case 'MODIFIED_BY':
                                    columns.push({
                                        label: 'modified-by',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'last_author__username',
                                        fixed_width: false,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? 'last_author__username' : order_by;
                                    break;
                                case 'ACTIVATED':
                                    columns.push({
                                        label: 'activated',
                                        field_name: preference.field_name,
                                        checked: preference.active,
                                        order_by: 'is_active',
                                        fixed_width: true,
                                        last_updated: false,
                                        width: null
                                        });
                                    order_by = preference.ordering_asc !== null ? 'is_active' : order_by;
                                    break;
                                default:
                                    break;
                            }
                            preferences.push(preference);
                            field_name.push(preference.field_name);
                            order = preference.ordering_asc !== null ? preference.ordering_asc ? 'asc' : 'desc' : order;
                        }
                    }
                });
                if (columns.length !== state.columns.length) {
                    state.columns.map((column) => {
                        let added = false;
                        for (let i = 0; i < columns.length; i++) {
                            if (columns[i].label === column.label) {
                                added = true;
                                break;
                            }
                        }
                        if (!added) {
                            columns.push({ ...column });
                        }
                    });
                }
                state.preferences = preferences;
                state.columns = columns;
                state.order_by = order_by;
                state.order = order;
            })
            .addDefaultCase(() => {});
    }
});

export const {
    addPreferences,
    moveDownColumn,
    moveUpColumn,
    resetFilters,
    resetDashboardSubscriptions,
    setEdit,
    setExportType,
    setLimit,
    setList,
    setOrderBy,
    setPage,
    setDashboardSubscriptions,
    setDashboardSubscriptionsPreferences,
    setRunning,
    setSearch,
    toggleColumn,
    toggleExport,
    toggleExportFull,
    toggleFilters,
    toggleRefresh,
    updateFilterCreatedDateGte,
    updateFilterCreator,
    updateFilterCreatedDateLte,
    updateFilterLastAuthor,
    updateFilterModifiedDateGte,
    updateFilterModifiedDateLte,
    updateFilterPeriod,
    updateFilterPeriodType,
    updateListItemActivated,
    setColumns,
    resizeColumn,
    addRecipient,
    removeRecipient,
} = dashboardSubscriptionsSlice.actions;

export default dashboardSubscriptionsSlice.reducer;